import { useNavigate } from 'react-router-dom'
import {
  useStartScreeningMutation,
  useTransitionFollowUpToStandardMutation,
} from '../../../api/ester-b2b-api/private/screening/screeningQueries'
import { queryErrorHandlerWithMessage } from '../../../utils/queryErrorHandler'
import { ScreeningType, isValidPersonalNumberFormat } from '@estercare/ester-shared'
import { usePersonalNumber } from '../../../hooks/usePersonalNumber'
import { useScreeningQuestionnaireStore, useScreeningStore } from '../../../state/store'
import { useTranslation } from 'react-i18next'
import { useSelectUserIsInvited, useSelectUserOrganisationName } from '../../../state/selectors'

export const useScreeningDescriptionText = (screeningType: ScreeningType, date?: string) => {
  const { t } = useTranslation()
  const organisationName = useSelectUserOrganisationName()
  const isInvited = useSelectUserIsInvited()

  const orgName = organisationName || t('employee.other.yourOrganisation')

  const descriptionMap: Record<ScreeningType, string> = {
    [ScreeningType.CHECK_IN]: t('employee.screening.checkInDescription', { date, organisationName: orgName }),
    [ScreeningType.FOLLOW_UP]: isInvited
      ? t('employee.screening.invitedFollowUpDescription', { date })
      : t('employee.screening.followUpDescription', { date, organisationName: orgName }),
    [ScreeningType.STANDARD]: isInvited
      ? t('employee.screening.invitedStandardDescription')
      : t('employee.screening.standardDescription', { organisationName: orgName }),
    [ScreeningType.CERTIFICATE]: '',
  }

  return descriptionMap[screeningType]
}

export const useStartScreening = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { mutateAsync: postStartScreening, isPending: isStartPending } = useStartScreeningMutation()
  const { savePersonalNumber } = usePersonalNumber()
  const screeningStore = useScreeningStore()
  const screeningQuestionnaireStore = useScreeningQuestionnaireStore()

  const startScreening = async (
    screeningCreationType: ScreeningType,
    setError: (error: string | null) => void,
    personalNumber?: string
  ) => {
    try {
      await postStartScreening(screeningCreationType, {
        onSuccess: () => {
          screeningStore.resetState()
          screeningQuestionnaireStore.resetState()
        },
      })
      navigate('/portal/health-services/screening/questions')

      if (personalNumber && personalNumber !== '') {
        const isPersonalNumberValid = isValidPersonalNumberFormat(personalNumber)
        savePersonalNumber({
          personalNumber,
          isValid: isPersonalNumberValid,
          setError,
          t,
        })
      }
    } catch (error) {
      const message = queryErrorHandlerWithMessage('start screening flow', t)(error as Error)
      setError(message)
    }
  }

  return { startScreening, isStartPending }
}

export const useTransitionFollowUpToStandard = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { mutateAsync: handleAndCreate, isPending: isStartPending } = useTransitionFollowUpToStandardMutation()
  const screeningStore = useScreeningStore()
  const screeningQuestionnaireStore = useScreeningQuestionnaireStore()
  const transitionFollowUpToStandard = async (
    latestScreeningId: string,
    setError: (error: string | null) => void = () => {}
  ) => {
    try {
      await handleAndCreate(
        { screeningId: latestScreeningId },
        {
          onSuccess: () => {
            screeningStore.resetState()
            screeningQuestionnaireStore.resetState()
          },
        }
      )
      navigate('/portal/health-services/screening/questions')
    } catch (error) {
      const message = queryErrorHandlerWithMessage('transition follow-up to standard', t)(error as Error)
      setError(message)
    }
  }

  return {
    transitionFollowUpToStandard,
    isStartPending,
  }
}
